import { IoGitNetworkOutline } from 'react-icons/io5';

import Platforms, { PlatformEnums } from 'utils/platforms';
import { SRT_TRUNK_TYPE_ID } from '@/constants/srt';
import {
    SIP_TRUNK_TYPE_NAME,
    trunkTypeDisplayName,
} from '@/constants/trunkTypes';
import {
    accordionCategory,
    findServiceCategory,
} from '../../components/AccordionCategory';
import CardDetails from '../../components/CardDetails';
import {
    ACS,
    DIALPAD,
    DIRECT_ROUTING,
    EIGHTBYEIGHT,
    GCC_HIGH,
    OPERATOR_CONNECT,
    RINGCENTRAL,
    SBC,
    SRT,
    WEBEX,
    ZOOM,
} from '../../constants';

// DashboardAccordion's heading for services no section lists.
export const UNGROUPED_SERVICES_TITLE = 'Services without Authentication';
export const OTHER_TITLE = 'Other';

const OPERATOR_PLATFORM_TITLE = 'Operator Platform';

// Most first; ties by `order`, then by name.
const byCount = (a, b) =>
    b.count - a.count ||
    (a.order === b.order ? a.title.localeCompare(b.title) : a.order - b.order);

/**
 * Counts rows per group, most first. Ties go by each group's `order`, then by
 * name; rows without a group are counted under `otherTitle`, last. Empty when
 * no row has a group, since a lone catch-all says nothing.
 *
 * @param {object[]|null} rows
 * @param {(row: object) => ?{ title: string, icon?: Function, image?: string, order?: number }} groupOf
 * @param {string} otherTitle
 * @returns {{ title: string, icon?: Function, image?: string, count: number }[]}
 */
const tally = (rows, groupOf, otherTitle = OTHER_TITLE) => {
    if (!Array.isArray(rows)) return [];

    const groups = new Map();
    let hasGroupedRow = false;
    rows.forEach((row) => {
        const group = groupOf(row);
        hasGroupedRow = hasGroupedRow || !!group;
        const {
            title,
            icon,
            image,
            order = 0,
        } = group ?? {
            title: otherTitle,
            order: Number.POSITIVE_INFINITY,
        };
        const counted = groups.get(title) ?? {
            title,
            icon,
            image,
            order,
            count: 0,
        };
        counted.count += 1;
        groups.set(title, counted);
    });

    if (!hasGroupedRow) return [];

    return [...groups.values()]
        .sort(byCount)
        .map(({ order: _order, ...group }) => group);
};

/**
 * The Services tile's breakdown: services per service status section, named
 * and iconed like the section headings, ties in the sections' order.
 *
 * @param {object[]|null} services - dashboard services; null while unknown
 * @param {string} [operatorPlatformName] - the company's brand name for the
 *   Operator Platform section
 */
export const getServiceBreakdown = (services, operatorPlatformName) =>
    tally(
        services,
        (service) => {
            const category = findServiceCategory(service);
            if (!category) return null;
            return {
                title:
                    category.title === OPERATOR_PLATFORM_TITLE &&
                    operatorPlatformName
                        ? operatorPlatformName
                        : category.title,
                icon: category.icon,
                image: category.image,
                order: accordionCategory.indexOf(category),
            };
        },
        UNGROUPED_SERVICES_TITLE,
    );

/**
 * The Users tile's breakdown: users per primary platform. The users list only
 * carries each user's primary platform, so a user on several platforms counts
 * once, under that one.
 *
 * @param {object[]|null} users - tcapUsers; null while unknown
 * @param {string} [operatorPlatformName] - Platforms has no name for the
 *   Operator Platform; it is branded per company
 */
export const getUserBreakdown = (users, operatorPlatformName) =>
    tally(users, (user) => {
        const platformID = user?.primaryUser?.platformID;
        const platform = Platforms[platformID];
        if (!platform) return null;
        return {
            title:
                platformID === PlatformEnums.OPERATOR_PLATFORM
                    ? operatorPlatformName || OPERATOR_PLATFORM_TITLE
                    : platform.name,
            image: platform.icon,
        };
    });

/**
 * The Numbers tile's breakdown: numbers per platform, leaving out platforms
 * with none. A number on more than one platform counts under each, so the
 * rows can add up to more than the tile.
 *
 * @param {{ platformID: string, name?: string, count: number }[]|undefined} platformCounts
 * @param {string} [operatorPlatformName] - Platforms has no name for the
 *   Operator Platform; it is branded per company
 */
export const getNumberBreakdown = (platformCounts, operatorPlatformName) =>
    (Array.isArray(platformCounts) ? platformCounts : [])
        .filter(({ count }) => count > 0)
        .map(({ platformID, name, count }) => {
            const platform = Platforms[platformID];
            return {
                title:
                    platformID === PlatformEnums.OPERATOR_PLATFORM
                        ? operatorPlatformName ||
                          name ||
                          OPERATOR_PLATFORM_TITLE
                        : platform?.name || name || OTHER_TITLE,
                image: platform?.icon,
                count,
                order: 0,
            };
        })
        .sort(byCount)
        .map(({ order: _order, ...group }) => group);

// The service each trunk type belongs to, for its logo. Trunk types carry no
// service type, so this goes by the backend's name, as the Trunks page does.
const TRUNK_TYPE_SERVICE_TYPES = {
    'MS Direct Routing': DIRECT_ROUTING,
    'MS Operator Connect': OPERATOR_CONNECT,
    'MS Direct Routing GCC High': GCC_HIGH,
    ACS,
    [SIP_TRUNK_TYPE_NAME]: SBC,
    'Zoom Provider Exchange': ZOOM,
    '8x8': EIGHTBYEIGHT,
    Webex: WEBEX,
    RingCentral: RINGCENTRAL,
    DialPad: DIALPAD,
};

// The logo of the service a trunk type belongs to, as its card shows it, or a
// generic one for a type with no service (Carrier - External).
const trunkTypeLogo = (trunkType) => {
    if (trunkType?.id === SRT_TRUNK_TYPE_ID) {
        return { image: CardDetails(SRT).icon };
    }
    if (trunkType?.name === OPERATOR_PLATFORM_TITLE) {
        return { image: Platforms[PlatformEnums.OPERATOR_PLATFORM].icon };
    }
    const serviceType = TRUNK_TYPE_SERVICE_TYPES[trunkType?.name];
    const image = serviceType && CardDetails(serviceType)?.icon;
    return image ? { image } : { icon: IoGitNetworkOutline };
};

/**
 * The Trunks tile's breakdown: trunks per trunk type, named as the Trunks
 * page names them and with their service's logo. Trunks carry no platform,
 * only their type.
 *
 * @param {object[]|null} trunks - null while unknown
 */
export const getTrunkBreakdown = (trunks) =>
    tally(trunks, (trunk) => {
        const title = trunkTypeDisplayName(trunk?.trunkType);
        return title ? { title, ...trunkTypeLogo(trunk.trunkType) } : null;
    });
